import { BaseClient } from "../../clients/base/BaseClient";
import { PENDING_ENDPOINTS } from "./pending.consts";
import type {
  PendingSamplesResponse,
  PendingSampleResultsResponse,
  PendingVisitsResponse,
  PendingVisitResultsResponse,
  PendingResultResponse,
  PendingFilters,
} from "./pending.types";

/**
 * Pending API Client
 * Handles all pending results and validation operations.
 */
export class PendingClient extends BaseClient {
  /**
   * Fetch filtered list of pending samples.
   * @param {PendingFilters} filters - Optional filter criteria.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<PendingSamplesResponse>} Array of pending samples.
   */
  async fetchPendingSamples(
    filters?: PendingFilters,
    signal?: AbortSignal | null,
  ): Promise<PendingSamplesResponse> {
    return this.get<PendingSamplesResponse>(PENDING_ENDPOINTS.SAMPLES, {
      params: filters,
      signal,
    });
  }

  /**
   * Fetch pending results for a specific sample.
   * @param {string} barcode - Sample barcode.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<PendingSampleResultsResponse>} Array of pending results.
   */
  async fetchPendingSampleResults(
    barcode: string,
    signal?: AbortSignal | null,
  ): Promise<PendingSampleResultsResponse> {
    return this.get<PendingSampleResultsResponse>(PENDING_ENDPOINTS.SAMPLE_BY_BARCODE(barcode), {
      signal,
    });
  }

  /**
   * Update sample barcode.
   * @param {string} oldBarcode - Original sample barcode.
   * @param {string} newBarcode - New sample barcode.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<void>}
   */
  async updateSampleBarcode(
    oldBarcode: string,
    newBarcode: string,
    signal?: AbortSignal | null,
  ): Promise<void> {
    return this.patch(PENDING_ENDPOINTS.UPDATE_SAMPLE_BARCODE(oldBarcode), {
      body: `"${newBarcode}"`,
      signal,
    });
  }

  /**
   * Delete all results for a sample.
   * @param {string} sampleId - Sample barcode.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<void>}
   */
  async deleteSample(sampleId: string, signal?: AbortSignal | null): Promise<void> {
    return this.delete(PENDING_ENDPOINTS.DELETE_SAMPLE(sampleId), {
      signal,
    });
  }

  /**
   * Upload all results for a sample to LIS.
   * @param {string} sampleId - Sample barcode.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<void>}
   */
  async publishSample(sampleId: string, signal?: AbortSignal | null): Promise<void> {
    return this.post(PENDING_ENDPOINTS.PUBLISH_SAMPLE(sampleId), {
      signal,
    });
  }

  /**
   * Fetch a specific result by ID.
   * @param {number} resultId - Result ID.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<PendingResultResponse>} Result details.
   */
  async fetchResultById(
    resultId: number,
    signal?: AbortSignal | null,
  ): Promise<PendingResultResponse> {
    return this.get<PendingResultResponse>(PENDING_ENDPOINTS.RESULT_BY_ID(resultId), {
      signal,
    });
  }

  /**
   * Delete a specific result.
   * @param {number} resultId - Result ID.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<void>}
   */
  async deleteResult(resultId: number, signal?: AbortSignal | null): Promise<void> {
    return this.delete(PENDING_ENDPOINTS.DELETE_RESULT(resultId), {
      signal,
    });
  }

  /**
   * Upload a specific result to LIS.
   * @param {number} resultId - Result ID.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<any>} API response (status, testId, error, etc.)
   */
  async publishResult(resultId: number, signal?: AbortSignal | null): Promise<any> {
    return this.post(PENDING_ENDPOINTS.PUBLISH_RESULT(resultId), {
      signal,
    });
  }

  /**
   * Fetch enum values for a specific test code.
   * @param {string} testCode - Test code.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<{ values: string[] }>} List of allowed enum values.
   */
  async fetchEnumValues(
    testCode: string,
    signal?: AbortSignal | null,
  ): Promise<{ testCode: string; values: string[] }> {
    return this.get<{ testCode: string; values: string[] }>(
      PENDING_ENDPOINTS.ENUM_VALUES(testCode),
      {
        signal,
      },
    );
  }

  /**
   * Update a result value.
   * @param {number} resultId - Result ID.
   * @param {string} newValue - New result value.
   * @param {string} editedBy - User who made the edit.
   * @param {string} reason - Optional reason for the edit.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<any>} Updated result data.
   */
  async updateResult(
    resultId: number,
    newValue: string,
    editedBy: string,
    reason?: string,
    signal?: AbortSignal | null,
  ): Promise<any> {
    return this.patch(PENDING_ENDPOINTS.UPDATE_RESULT(resultId), {
      body: JSON.stringify({
        newValue,
        editedBy,
        reason,
      }),
      signal,
    });
  }

  /**
   * Fetch filtered list of pending visits.
   * @param {Omit<PendingFilters, 'filterUnknown'>} filters - Optional filter criteria.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<PendingVisitsResponse>} Array of pending visits.
   */
  async fetchPendingVisits(
    filters?: Omit<PendingFilters, "filterUnknown">,
    signal?: AbortSignal | null,
  ): Promise<PendingVisitsResponse> {
    return this.get<PendingVisitsResponse>(PENDING_ENDPOINTS.VISITS, {
      params: filters,
      signal,
    });
  }

  /**
   * Fetch pending results for a specific visit.
   * @param {string} visitId - Visit ID.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<PendingVisitResultsResponse>} Array of pending results for the visit.
   */
  async fetchPendingVisitResults(
    visitId: string,
    signal?: AbortSignal | null,
  ): Promise<PendingVisitResultsResponse> {
    return this.get<PendingVisitResultsResponse>(PENDING_ENDPOINTS.VISIT_BY_ID(visitId), {
      signal,
    });
  }
}
